/**
 * Sized copies of a public logo or cover — `/public-objects/<key>?w=<width>`
 * (server/lib/publicObjects.ts, `VARIANT_WIDTHS`).
 *
 * Every picture used to be drawn from its full stored size: a 274 KB logo PNG
 * in a 48px disc, a 2800×800 cover in a 480px card. The bytes were cached, but
 * the browser decoded the whole picture again on every mount, which is what
 * "the images reload from scratch every time" was (Andrew, 2026-09-29). A
 * variant is a WebP at the width asked for, made once and cached at the edge
 * for a year.
 *
 * Only for keys that HAVE variants: public objects (not `Private Objects/` or
 * `projects/{id}/`, which the server authorizes per request), not SVG, and not
 * a URL that already carries a query. Anything else comes back unchanged — a
 * static /salefish-logo-light.png, a blob: preview, a data: URL.
 */
export const VARIANT_WIDTHS = [64, 128, 256, 512, 1024, 2048] as const;

export function hasVariants(src: string | null | undefined): src is string {
  if (!src || src.includes("?")) return false;
  // An Agent Portal's own file link, answered by Console at the same widths
  // (server/agentPortal/files.ts): QMI photos, community covers.
  if (/^\/api\/files\/uploads(%2F|\/)/i.test(src)) return !/\.svg$/i.test(src);
  if (!src.startsWith("/public-objects/")) return false;
  if (/(^|\/)Private Objects\//i.test(decodeURI(src))) return false;
  if (/^\/public-objects\/projects\//i.test(src)) return false;
  return !/\.svg$/i.test(src);
}

/** The smallest variant at least `cssWidth × dpr` wide (or the largest there is). */
export function imageVariant(src: string, cssWidth: number, dpr?: number): string;
export function imageVariant(src: string | null | undefined, cssWidth: number, dpr?: number): string | undefined;
export function imageVariant(src: string | null | undefined, cssWidth: number, dpr = 2): string | undefined {
  if (!src) return undefined;
  if (!hasVariants(src)) return src;
  const need = cssWidth * dpr;
  const width = VARIANT_WIDTHS.find((w) => w >= need) ?? VARIANT_WIDTHS[VARIANT_WIDTHS.length - 1];
  return `${src}?w=${width}`;
}

/** A `srcset` of the given variant widths, for an image whose drawn width varies. */
export function imageSrcSet(src: string | null | undefined, widths: readonly number[]): string | undefined {
  if (!hasVariants(src)) return undefined;
  return widths.map((w) => `${src}?w=${w} ${w}w`).join(", ");
}
